import { FeaturePageLayout } from '../../components/features/FeaturePageLayout';
import { StressTestMockup, ForecastMockup } from '../../components/features/ProductMockups';

const RELATED = [
  { label: 'Prévision de trésorerie', href: '/fonctionnalites/prevision-tresorerie' },
  { label: 'Suivi des subventions', href: '/fonctionnalites/suivi-subventions' },
  { label: 'Alertes', href: '/fonctionnalites/alertes-tresorerie' },
  { label: 'Reporting financier', href: '/fonctionnalites/reporting-financier' },
];

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function StressTestTresoreriePage({ onNavigate, onSignIn, onSignUp }: Props) {
  return (
    <FeaturePageLayout
      seo={{
        title: 'Stress test de trésorerie — Évaluez votre résistance financière | TrezoOne',
        description: 'Testez la solidité financière de votre structure face à un retard de subvention, une baisse de revenus ou un choc financier. Calculez votre Temps de Survie Financière avec TrezoOne.',
        canonical: '/fonctionnalites/stress-test-tresorerie',
        keywords: 'stress test trésorerie, temps de survie financière, burn rate, fonds de roulement dynamique, résistance financière',
      }}
      activeNavId="stress-test"
      hero={{
        tag: 'Stress test de trésorerie',
        title: 'Combien de temps votre structure tiendra-t-elle en cas de choc ?',
        subtitle: 'Simulez un retard de subvention, une perte de revenus ou un choc financier. Obtenez instantanément votre Temps de Survie Financière, votre Burn Rate et la date estimée de tension.',
        mockup: <StressTestMockup />,
      }}
      problem={{
        title: 'Vous ne savez pas à quel point votre structure est fragile financièrement',
        points: [
          'Votre budget est équilibré sur le papier, mais un simple retard de subvention suffit à créer une crise.',
          'Vous n\'avez jamais calculé combien de temps vous tiendrez sans encaissements.',
          'Vous découvrez les risques trop tard, sans temps pour réagir.',
          'Vos financeurs vous posent des questions sur votre solvabilité et vous n\'avez pas les données pour répondre.',
        ],
      }}
      steps={[
        { num: 1, title: 'Définissez votre scénario', desc: 'Choisissez le type de choc : retard de subvention, baisse de revenus, dépense exceptionnelle imprévue.' },
        { num: 2, title: 'Paramétrez l\'intensité', desc: 'Indiquez le montant et la durée du choc simulé. TrezoOne ajuste le calcul en temps réel.' },
        { num: 3, title: 'Obtenez votre diagnostic', desc: 'Consultez votre TSF, votre Burn Rate, la date de tension estimée et votre niveau de risque (vert, orange, rouge).' },
        { num: 4, title: 'Prenez vos mesures préventives', desc: 'TrezoOne vous suggère les actions prioritaires pour renforcer votre résilience financière.' },
      ]}
      screens={[<StressTestMockup />, <ForecastMockup />]}
      useCases={[
        { audience: 'Associations', benefit: 'Évaluez votre résistance avant de déposer votre prochain dossier de subvention.' },
        { audience: 'PME', benefit: 'Anticipez l\'impact d\'un client impayé ou d\'un retard de livraison sur vos flux.' },
        { audience: 'Conseil d\'administration', benefit: 'Présentez une évaluation des risques financiers objective à vos membres.' },
        { audience: 'RAF / DAF', benefit: 'Justifiez vos réserves de trésorerie et vos demandes de financement auprès des dirigeants.' },
        { audience: 'Dirigeant', benefit: 'Prenez conscience des risques réels et dormez plus sereinement.' },
        { audience: 'Financeurs', benefit: 'Montrez que vous pilotez votre structure de manière professionnelle et responsable.' },
      ]}
      benefits={[
        'Connaître votre Temps de Survie Financière précis à tout moment',
        'Anticiper une crise de trésorerie 2 à 3 mois avant qu\'elle arrive',
        'Justifier vos réserves de trésorerie auprès de vos financeurs',
        'Renforcer votre dossier de demande de subvention',
        'Rassurer votre conseil d\'administration avec des données objectives',
        'Prendre des décisions de recrutement ou d\'investissement éclairées',
      ]}
      faq={[
        { q: 'Qu\'est-ce que le Temps de Survie Financière (TSF) ?', a: 'Le TSF est le nombre de jours pendant lesquels votre structure peut continuer à fonctionner avec sa trésorerie actuelle, sans aucun nouvel encaissement.' },
        { q: 'Comment est calculé le Burn Rate ?', a: 'Le Burn Rate est la dépense quotidienne moyenne calculée sur vos 3 derniers mois de décaissements. Il mesure votre rythme de consommation de trésorerie.' },
        { q: 'Qu\'est-ce que le Fonds de Roulement Dynamique (FRD) ?', a: 'Le FRD est le montant de trésorerie recommandé pour couvrir vos charges sur une période de sécurité donnée (généralement 30 à 90 jours).' },
        { q: 'Peut-on simuler plusieurs scénarios ?', a: 'Oui. Vous pouvez créer et comparer plusieurs scénarios de stress simultanément pour évaluer différentes hypothèses de risque.' },
      ]}
      relatedLinks={RELATED}
      onNavigate={onNavigate}
      onSignIn={onSignIn}
      onSignUp={onSignUp}
    />
  );
}
